import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../contexts/AuthContext";
import { ticketService } from "../../../services/api/ticketService";
import { formatDate } from "../../../utils/DateFormat";
// import Loading from "../components/Loading";
import { AnimatePresence, motion } from "framer-motion";
import { StatusBadge } from "../../../constants/statusConstants";
import MobilePatternHeader from "../components/MobilePatternHeader";

const pageVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.15 } },
};

const cardVariant = {
  hidden: { opacity: 0, y: 8, scale: 0.98 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.18, delay: i * 0.04 },
  }),
};

const RowSkeleton = () => (
  <div className="animate-pulse space-y-3">
    <div className="h-4 w-1/3 bg-gray-200 rounded" />
    <div className="h-10 w-full bg-gray-200 rounded" />
  </div>
);

const DetailSkeleton = ({ isMobile }) => (
  <div className={`${isMobile ? "p-3" : "p-6"} space-y-4`}>
    <RowSkeleton />
    <RowSkeleton />
    <RowSkeleton />
    <div className="h-24 bg-gray-200 rounded animate-pulse" />
  </div>
);

const truncate = (s, n = 10) =>
  s && s.length > n ? s.slice(0, n) + "..." : s || "";

const TICKET_STATUS_MAP = {
  pending: "Chờ phản hồi",
  "in-progress": "Đang xử lý",
  resolved: "Đã hoàn thiện",
};

const DetailTicket = ({ isMobile }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token_client, user_client } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const safeUser = useMemo(() => {
    const u = user_client || {};
    return {
      avatar: u.avatar || "/images/avatar-default.png",
      username: u.username || "",
      fullName: u.fullName || "",
      companyName: u.companyName || "",
      email: u.email || "",
    };
  }, [user_client]);

  const formatTicket = (resp) => {
    return {
      answers: resp.adminResponse || "",
      isAnswers: !!resp.isAnswered,
      name: resp.name || "",
      phone: resp.phoneNumber || "",
      email: resp.email || "",
      profileID: resp.profileID || null,
      date: resp.createdAt ? formatDate(resp.createdAt) : "N/A",
      content: resp.message || "No content",
      status: resp.status || "pending",
    };
  };

  const fetchTicket = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await ticketService.get_ticket_by_id(id, token_client);
      setData(formatTicket(resp));
    } catch (error) {
      toast.dismiss();
      toast.error("Lỗi khi lấy dữ liệu ticket từ server.");
      console.error("get_ticket_by_id error:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, token_client]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  if (loading) {
    return (
      <div
        className={`flex ${
          isMobile ? "h-screen w-screen" : "h-full"
        } items-center justify-center`}
      >
        <DetailSkeleton isMobile />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-gray-600">Không tìm thấy dữ liệu ticket.</p>
      </div>
    );
  }

  if (isMobile) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="ticket-mobile"
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="h-full w-full"
        >
          <MobilePatternHeader
            title="Chi tiết yêu cầu"
            onBack={() => navigate(-1)}
          />

          <div className="h-[calc(100%-64px)] bg-[#F2F2F2] p-3">
            <motion.div
              className="w-full max-h-[calc(100%-80px)] overflow-auto bg-white rounded-lg flex flex-col p-3 border border-[#EAEAEA] space-y-3"
              initial="hidden"
              animate="show"
              variants={cardVariant}
            >
              <div className="flex items-center justify-between w-full">
                <div className="space-y-2">
                  <p className="text-[12px] opacity-40">Ngày tạo ticket</p>
                  <div className="flex gap-2 items-center">
                    <img
                      src="/icons/calendar.svg"
                      alt="calendar icon"
                      className="w-5 h-5"
                    />
                    <p className="text-[14px] font-sf-medium">{data.date}</p>
                  </div>
                </div>
                <div className="flex flex-col justify-center space-y-2 items-end">
                  <p className="text-[12px] opacity-40">Trạng thái</p>
                  <StatusBadge
                    status={TICKET_STATUS_MAP[data.status] || data.status}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-[12px] opacity-40">Tài khoản</p>
                <div className="flex gap-2 items-center">
                  <img
                    src={safeUser.avatar}
                    alt="user avatar"
                    className="rounded-lg size-[40px] object-cover"
                  />
                  <div className="flex flex-col justify-center">
                    <p
                      className="text-[14px] font-sf-medium"
                      title={
                        safeUser.username ||
                        safeUser.fullName ||
                        safeUser.companyName
                      }
                    >
                      {truncate(
                        safeUser.username ||
                          safeUser.fullName ||
                          safeUser.companyName,
                      )}
                    </p>
                    <p className="text-[12px] opacity-70">{safeUser.email}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <p className="text-[14px] text-[#0D1829] font-sf-medium">
                  Nội dung
                </p>
                <p className="text-[14px] text-gray-800">{data.content}</p>
              </div>

              {data.isAnswers && (
                <div className="flex flex-col justify-center">
                  <p className="text-[14px] text-[#0D1829] font-sf-medium">
                    Trả lời
                  </p>
                  <p className="text-[14px] text-gray-800">{data.answers}</p>
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="ticket-desktop"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="h-full overflow-auto"
      >
        <div className=" bg-white rounded-[16px] border border-[#D2D5DA] p-4 h-full">
          <h1 className="text-center font-sf-semibold text-[#0457C2] text-2xl uppercase border-b p-4">
            Chi tiết yêu cầu
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 items-start justify-between px-[5vw] py-6">
            <div className="flex items-start gap-3 min-w-0">
              <strong>Họ và tên:</strong>
              <span className="truncate">{data.name}</span>
            </div>
            <div className="flex items-start gap-3 min-w-0">
              <strong>Số điện thoại:</strong>
              <span className="truncate">{data.phone}</span>
            </div>
            <div className="flex items-start gap-3 min-w-0">
              <strong>Email:</strong>
              <span className="truncate">{data.email}</span>
            </div>

            <div className="flex items-start gap-3 min-w-0">
              <strong>Ngày tạo ticket:</strong>
              <span className="truncate">{data.date}</span>
            </div>
            <div className="flex items-center gap-3">
              <strong>Trạng thái:</strong>
              <StatusBadge
                status={TICKET_STATUS_MAP[data.status] || data.status}
                className="min-w-[120px]"
              />
            </div>

            {data.profileID && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/quan-ly-ho-so/ho-so/${data.profileID}`)
                }
                className="w-[180px] justify-center inline-flex items-center gap-2 text-sm py-2 hover:bg-blue-50 bg-blue-500 text-white text-center rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 transition-colors"
              >
                Đi tới hồ sơ
                <img src="/icons/right-arrow-circle.svg" alt="" />
              </button>
            )}
          </div>

          <div className="px-[5vw] pb-6">
            <strong>Nội dung:</strong>
            <p className="p-4 border rounded-lg mt-2 min-h-32 whitespace-pre-wrap">
              {data.content}
            </p>
          </div>

          {data.isAnswers && (
            <div className="px-[5vw] pb-6">
              <strong>Trả lời:</strong>
              <p className="p-4 border rounded-lg mt-2 min-h-32 whitespace-pre-wrap">
                {data.answers}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DetailTicket;
